<script setup>
import { computed, onMounted, ref } from 'vue'
import { Config } from '@/Config'
import * as Server from '@/services/Server'
import PageHeader from '@/components/PageHeader.vue'
import RankingTable from '@/components/RankingTable.vue'
import RecentRaces from '@/components/RecentRaces.vue'
import SeasonStandings from '@/components/SeasonStandings.vue'

/** Page size of the ranking table (see Config.standings). */
const { perPage } = Config.standings

/**
 * The page payload, in one call: the ranking (one page at a time, the page number living here
 * because it drives the request) and the general standings of the running season, which the server
 * sends in the same response because the two blocks are read together (`season`, null when no
 * season is running). One request and one cache entry for what the page opens on.
 */
const page = ref(1)
const standing = ref({ players: [], me: null, total: 0, lastPage: 1, season: null, period: null })
const standingLoading = ref(true)
const standingError = ref(false)

/**
 * Whether the season block belongs on the page: while loading it does (the placeholder has the
 * height of the table), after that only if a season is running. With no season the ranking takes
 * the whole width instead of sitting next to an empty column.
 */
const season = computed(() => standing.value.season)
const hasSeason = computed(() => standingLoading.value || season.value !== null)

/**
 * The latest races, asked for only when the feed comes into view (see the observer in the
 * template): they sit under the two blocks, so opening the page does not pay for a block nobody has
 * scrolled to yet. Until then the block holds its place with its own skeleton.
 *
 * The feed is paginated server-side: the client asks for a page number and the server decides how
 * many races go in it. `racesLastPage` is where the feed ends, so "load more" disappears once the
 * last page is on screen; `racesPerPage` labels the button.
 */
const races = ref([])
const racesPage = ref(0)
const racesLastPage = ref(1)
const racesPerPage = ref(0)
const racesLoading = ref(true)
const racesError = ref(false)
const racesRequested = ref(false)
const racesLoadingMore = ref(false)
const racesMoreError = ref(false)

const hasMoreRaces = computed(() => racesPage.value < racesLastPage.value)

/** Page bookkeeping shared by the two calls: how big a page is and where the feed ends. */
const applyRacesMeta = (response) => {
  racesPage.value = response.data?.meta?.page ?? racesPage.value
  racesLastPage.value = response.data?.meta?.lastPage ?? racesLastPage.value
  racesPerPage.value = response.data?.meta?.perPage ?? racesPerPage.value
}

const loadStanding = () => {
  standingLoading.value = true
  standingError.value = false

  Server.get(Config.server.routes.api.standings(page.value, perPage))
    .then(response => {
      standing.value = response.data?.data ?? standing.value
    })
    .catch(() => {
      standing.value = { players: [], me: null, total: 0, lastPage: 1, season: null, period: null }
      standingError.value = true
    })
    .finally(() => {
      standingLoading.value = false
    })
}

const goToPage = (value) => {
  page.value = value
  loadStanding()
}

const loadRaces = () => {
  // The observer fires again every time the block leaves and comes back: the feed is asked for once.
  if (racesRequested.value) {
    return
  }
  racesRequested.value = true

  racesLoading.value = true
  racesError.value = false

  Server.get(Config.server.routes.api.latest_races(1))
    .then(response => {
      races.value = response.data?.data ?? []
      applyRacesMeta(response)
    })
    .catch(() => {
      races.value = []
      racesError.value = true
    })
    .finally(() => {
      racesLoading.value = false
    })
}

/** Appends the next page of the feed; the server says whether one is left (see applyRacesMeta). */
const loadMoreRaces = () => {
  if (racesLoadingMore.value) {
    return
  }

  racesLoadingMore.value = true
  racesMoreError.value = false

  Server.get(Config.server.routes.api.latest_races(racesPage.value + 1))
    .then(response => {
      races.value = [...races.value, ...(response.data?.data ?? [])]
      applyRacesMeta(response)
    })
    .catch(() => {
      racesMoreError.value = true
    })
    .finally(() => {
      racesLoadingMore.value = false
    })
}

const onRacesIntersect = (isIntersecting) => {
  if (isIntersecting) {
    loadRaces()
  }
}

onMounted(() => {
  loadStanding()

  // Without IntersectionObserver the directive does nothing and the feed would sit on its
  // placeholder forever.
  if (!('IntersectionObserver' in window)) {
    loadRaces()
  }
})
</script>

<template>
  <main>
    <v-container>
      <PageHeader
        :title="$t('navigation.standings')"
        :subtitle="$t('standings.subtitle')"
        icon="mdi-trophy-variant"
      />

      <v-alert
        type="warning"
        variant="tonal"
        class="mb-4"
        :text="$t('leaderboard.developmentWarning')"
      />

      <!-- The season on the left (it is the event in progress, the block that changes), the overall
           ranking on the right: the two come from the same response. On small screens the two
           stack, the season on top. -->
      <v-row>
        <v-col v-if="hasSeason" cols="12" md="6">
          <SeasonStandings
            class="h-100"
            :season="season"
            :rows="perPage"
          />
        </v-col>

        <v-col :cols="12" :md="hasSeason ? 6 : 12">
          <v-alert v-if="standingError" type="error" variant="tonal">
            {{ $t('standings.loadError') }}
          </v-alert>
          <RankingTable
            v-else
            class="h-100"
            :players="standing.players"
            :me="standing.me"
            :loading="standingLoading"
            :page="page"
            :last-page="standing.lastPage"
            :total="standing.total"
            :per-page="perPage"
            :period="standing.period"
            @update:page="goToPage"
          />
        </v-col>
      </v-row>

      <!-- Latest races played: the block is under the two above, so its request starts only when it
           comes into view instead of on page load (the observer is the block itself, which keeps
           the room for what is coming with its own skeleton). The feed is longer than one page: the
           older races arrive on demand, at the bottom of the block. -->
      <div v-intersect="onRacesIntersect">
        <v-alert v-if="racesError" type="error" variant="tonal" class="mt-6">
          {{ $t('races.loadError') }}
        </v-alert>
        <template v-else>
          <RecentRaces class="mt-6" :races="races" :loading="racesLoading" />

          <div v-if="hasMoreRaces" class="d-flex justify-center mt-4">
            <v-btn
              variant="tonal"
              prepend-icon="mdi-history"
              :loading="racesLoadingMore"
              @click="loadMoreRaces"
            >
              {{ $t('races.loadMore', { count: racesPerPage }) }}
            </v-btn>
          </div>

          <v-alert v-if="racesMoreError" type="error" variant="tonal" class="mt-4">
            {{ $t('races.loadError') }}
          </v-alert>
        </template>
      </div>
    </v-container>
  </main>
</template>
